import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { ArrowBack, ArrowForward, Groups, Handshake, Psychology } from '@mui/icons-material';
import { Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { useApi } from '@shared/services/api';
import { commonButtons } from '@shared/theme/patterns';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { OnboardingOptionItem } from './OnboardingOptionItem';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface UseCasesStepProps {
  onNext: (selectedUseCases: string[]) => void;
  onBack?: () => void;
  onSkip?: () => void;
  onSelectionChange?: (selectedUseCases: string[]) => void;
  initialSelectedUseCases?: string[];
  isFirstStep?: boolean;
}

const USE_CASE_OPTIONS = [
  {
    description: 'Research and build a better perspective on your account territory',
    icon: Psychology,
    id: 'research-territory',
    label: 'I am here to research and build a better perspective on my account territory',
  },
  {
    description: 'Connect with other Account Executives who can share knowledge on closing target prospects',
    icon: Groups,
    id: 'connect-aes',
    label:
      'I am here to connect with other Account Executives who can share knowledge with me on how to close my target prospects',
  },
  {
    description: 'Share knowledge on breaking into enterprise accounts through paid consulting opportunities',
    icon: Handshake,
    id: 'consulting',
    label:
      'I am here to share my knowledge on how to break into enterprise accounts through paid consulting opportunities',
  },
];

const RESEARCH_USE_CASES = ['research-territory', 'connect-aes'];
const CONTRIBUTOR_USE_CASES = ['consulting'];

interface UseCasesStepInnerProps extends UseCasesStepProps {
  hasStripeSetUp: boolean;
}

const UseCasesStepInner: React.FC<UseCasesStepInnerProps> = ({
  onNext,
  onBack,
  onSkip,
  onSelectionChange,
  initialSelectedUseCases = [],
  isFirstStep = false,
  hasStripeSetUp,
}) => {
  const api = useApi();
  const stripe = useStripe();
  const elements = useElements();
  const { user, refetchUser } = useContext(UserContext);

  const [selectedUseCases, setSelectedUseCases] = useState<string[]>(initialSelectedUseCases);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hourlyRate, setHourlyRate] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string>('');

  const showHourlyRateForm = selectedUseCases.some((uc) => CONTRIBUTOR_USE_CASES.includes(uc));
  const hasResearchOption = selectedUseCases.some((uc) => RESEARCH_USE_CASES.includes(uc));
  const showPaymentForm = hasResearchOption && !hasStripeSetUp;
  const paymentRequired = showPaymentForm && !cardComplete;
  const hourlyRateRequired = showHourlyRateForm && (!hourlyRate || Number.parseFloat(hourlyRate) <= 0);
  const noSelectionMade = selectedUseCases.length === 0;
  const showBothWidgets = showPaymentForm && showHourlyRateForm;

  useEffect(() => {
    onSelectionChange?.(selectedUseCases);
  }, [selectedUseCases, onSelectionChange]);

  const handleToggle = (useCaseId: string) => {
    setSelectedUseCases((prev) => {
      const newSelection = prev.includes(useCaseId) ? prev.filter((id) => id !== useCaseId) : [...prev, useCaseId];
      return newSelection;
    });
    setCardError('');
  };

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setCardComplete(event.complete);
    setCardError(event.error?.message ?? '');
  };

  const handleContinue = async () => {
    if (!user?.id) {
      onNext(selectedUseCases);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (showPaymentForm) {
        if (!stripe || !elements) {
          setError('Stripe is not ready. Please wait a moment and try again.');
          setSaving(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setError('Card element not found. Please refresh and try again.');
          setSaving(false);
          return;
        }

        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (stripeError) {
          setCardError(stripeError.message ?? 'Card verification failed. Please try again.');
          setSaving(false);
          return;
        }

        const setupResult = await api.post('subscription/setup-stripe', {
          paymentMethodId: paymentMethod.id,
        });

        if (setupResult?.error) {
          setError('Failed to set up billing. Please try again.');
          setSaving(false);
          return;
        }
      }

      const response = await api.put(`user/${user.id}/use-cases`, {
        useCases: selectedUseCases,
      });

      if (response.error) {
        setError(response.message || 'Failed to save use cases');
        setSaving(false);
        return;
      }

      const role = hasResearchOption && showHourlyRateForm ? 'both' : hasResearchOption ? 'researcher' : 'contributor';
      await api.put('user', { id: user.id, role });

      if (showHourlyRateForm && hourlyRate) {
        const rate = Number.parseFloat(hourlyRate);
        if (!isNaN(rate) && rate > 0) {
          await api.put('user', { id: user.id, hourlyRate: rate });
        }
      }

      await refetchUser();
      onNext(selectedUseCases);
    } catch (err) {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onNext(selectedUseCases);
    }
  };

  return (
    <div className="onboarding-step" data-testid="use-cases-step">
      <div className="onboarding-step__content">
        {isFirstStep && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6, ease: 'easeOut' }}
          >
            <h3 className="onboarding-step__welcome-title">Welcome to Kandir! 🚀</h3>
          </motion.div>
        )}

        {isFirstStep && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
          >
            <p className="onboarding-step__welcome-subtitle">
              Let's get you set up in under 2 minutes. We'll personalize your experience so you can start closing deals
              faster.
            </p>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: isFirstStep ? 0.8 : 0.1,
            duration: 0.6,
            ease: 'easeOut',
          }}
        >
          <h4 className="onboarding-step__section-title">What brings you to Kandir?</h4>
        </motion.div>

        <div className={`onboarding-use-cases${showBothWidgets ? ' onboarding-use-cases--stacked' : ''}`}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: isFirstStep ? 1.0 : 0.3,
              duration: 0.6,
              ease: 'easeOut',
            }}
            className="onboarding-use-cases__list"
          >
            {USE_CASE_OPTIONS.map((useCase) => {
              const isSelected = selectedUseCases.includes(useCase.id);
              const IconComponent = useCase.icon;

              return (
                <OnboardingOptionItem
                  key={useCase.id}
                  isSelected={isSelected}
                  onClick={() => handleToggle(useCase.id)}
                  icon={<IconComponent color={isSelected ? 'primary' : 'action'} />}
                  label={useCase.label}
                />
              );
            })}
          </motion.div>

          {showPaymentForm && !showBothWidgets && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="onboarding-payment"
            >
              <div className="onboarding-payment__header">
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  Start your 7-day free trial
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Full access, no charge today — $49/month after the trial. Cancel any time.
                </Typography>
              </div>
              <div className="stripe-card-wrapper">
                <CardElement
                  options={{
                    style: {
                      base: {
                        color: '#2a2018',
                        fontFamily: '"Inter", "Plus Jakarta Sans", sans-serif',
                        fontSize: '15px',
                        fontSmoothing: 'antialiased',
                        '::placeholder': { color: '#9ca3af' },
                      },
                      invalid: { color: '#dc2626', iconColor: '#dc2626' },
                    },
                    hidePostalCode: false,
                  }}
                  onChange={handleCardChange}
                />
              </div>
              {cardError && (
                <Typography variant="caption" color="error" display="block" mt={0.5}>
                  {cardError}
                </Typography>
              )}
              <div className="payment-security-note" style={{ marginTop: '8px' }}>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span>Secured by Stripe. We never store your card details.</span>
              </div>
            </motion.div>
          )}

          {showHourlyRateForm && !showBothWidgets && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="onboarding-hourly-rate"
            >
              <h5 className="onboarding-hourly-rate__title">Set your hourly rate</h5>
              <p className="onboarding-hourly-rate__subtitle">
                This helps us match you with the right consulting opportunities.
              </p>
              <TextField
                fullWidth
                label="Hourly Rate"
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder="150"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 1 }}
              />
            </motion.div>
          )}

          {showBothWidgets && (
            <div className="onboarding-use-cases__widgets-row">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="onboarding-payment"
              >
                <div className="onboarding-payment__header">
                  <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                    Start your 7-day free trial
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    Full access, no charge today — $49/month after the trial. Cancel any time.
                  </Typography>
                </div>
                <div className="stripe-card-wrapper">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          color: '#2a2018',
                          fontFamily: '"Inter", "Plus Jakarta Sans", sans-serif',
                          fontSize: '15px',
                          fontSmoothing: 'antialiased',
                          '::placeholder': { color: '#9ca3af' },
                        },
                        invalid: { color: '#dc2626', iconColor: '#dc2626' },
                      },
                      hidePostalCode: false,
                    }}
                    onChange={handleCardChange}
                  />
                </div>
                {cardError && (
                  <Typography variant="caption" color="error" display="block" mt={0.5}>
                    {cardError}
                  </Typography>
                )}
                <div className="payment-security-note" style={{ marginTop: '8px' }}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span>Secured by Stripe. We never store your card details.</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="onboarding-hourly-rate"
              >
                <h5 className="onboarding-hourly-rate__title">Set your hourly rate</h5>
                <p className="onboarding-hourly-rate__subtitle">
                  This helps us match you with the right consulting opportunities.
                </p>
                <TextField
                  fullWidth
                  label="Hourly Rate"
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="150"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 1 }}
                />
              </motion.div>
            </div>
          )}
        </div>

        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <p className="text-error mt-16" style={{ textAlign: 'center' }}>
              {error}
            </p>
          </motion.div>
        )}
      </div>

      <div className="onboarding-step__actions">
        {onBack && (
          <Button onClick={onBack} startIcon={<ArrowBack />} color="inherit" disabled={saving}>
            Back
          </Button>
        )}
        <div className="onboarding-step__actions-right">
          <Button
            data-testid="use-cases-step-continue-button"
            onClick={handleContinue}
            color="primary"
            variant="contained"
            endIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
            disabled={saving || noSelectionMade || paymentRequired || hourlyRateRequired}
            sx={commonButtons.cta}
          >
            {saving ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const UseCasesStep: React.FC<UseCasesStepProps> = (props) => {
  const { user } = useContext(UserContext);
  const hasStripeSetUp = !!(user?.accountMemberships?.[0]?.account as any)?.stripeCustomerId;

  return (
    <Elements stripe={stripePromise}>
      <UseCasesStepInner {...props} hasStripeSetUp={hasStripeSetUp} />
    </Elements>
  );
};
